import {
  Component,
  ViewChild,
  Input,
  forwardRef,
  OnChanges,
  SimpleChanges,
  Inject,
  LOCALE_ID,
  OnInit,
  OnDestroy
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MultiSelectComponent } from './multi-select.component';
import { CommonService } from '../../services/common/common.service';

/* Converts user-entered text to values, breaking on blur, enter, tab, or specified separators. */
/* Examples:
<ssg-multi-text
  placeholder="Enter names..."
  [autoSeparators]="[',',';']"
  [(ngModel)]="selectedItems">
</ssg-multi-text>
*/
@Component({
  selector: 'ssg-multi-text',
  templateUrl: './multi-text.component.html',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiTextComponent),
    multi: true
  }],
})
export class MultiTextComponent implements OnChanges, ControlValueAccessor, OnInit, OnDestroy {
  private _caseSensitive: boolean;
  private _multiSelectComponent: MultiSelectComponent;

  private _keydownEventHandler;

  dynamicItems = [];

  @Input() autoSeparators = [','];
  @Input() placeholder: string;
  @Input() disabled: boolean;

  @Input() set caseSensitive(value: boolean) {
    this._caseSensitive = value;
    this._syncComponent();
  }
  get caseSensitive(): boolean {
    return this._caseSensitive;
  }

  @ViewChild(MultiSelectComponent, { static: true }) set multiSelectComponent(value: MultiSelectComponent) {
    this._multiSelectComponent = value;
    this._syncComponent();
  }
  get multiSelectComponent(): MultiSelectComponent {
    return this._multiSelectComponent;
  }

  get selectedValues() {
    return this._multiSelectComponent.selectedValues;
  }

  get hasValue() {
    return this._multiSelectComponent.hasValue;
  }

  private get _nativeInput(): any {
    return this._multiSelectComponent.selectComponent.searchInput.nativeElement;
  }

  constructor(@Inject(LOCALE_ID) private readonly _locale: string) { }

  ngOnInit() {
    this._multiSelectComponent.selectComponent.handleMousedown = this.onMouseDown;

    this._keydownEventHandler = this.onKeyDown.bind(this);
    this._nativeInput.addEventListener('keydown', this._keydownEventHandler);

    this._multiSelectComponent.selectComponent.typeahead = null;
    this._multiSelectComponent.selectComponent.addTag = false;
  }

  ngOnDestroy() {
    if (this._keydownEventHandler) {
      this._nativeInput.removeEventListener('keydown', this._keydownEventHandler);
      this._keydownEventHandler = null;
    }
  }

  onBlur(data) {
    this._convertTextToSelectedItems();
  }

  onAdd(data) {
    this._reIndex();
    this._convertTextToSelectedItems();
  }

  onRemove(data) {
    this._reIndex();
  }

  onMouseDown(data) {
    const target = data.target as HTMLElement;

    if (!document.activeElement === this._nativeInput) {
      this._multiSelectComponent.selectComponent.focus();
      data.preventDefault();
    }

    if (target.className.includes('ng-value-icon')) {
      data.preventDefault();
      return;
    }
  }

  onKeyDown(data) {
    const currentText = this._nativeInput.value as string;

    switch (data.which) {
      case 8: // Backspace
        // Delete the last entered item, IF there isn't any text already.
        if (!currentText && this.hasValue) {
          this._multiSelectComponent.selectComponent.unselect(this._multiSelectComponent.selectComponent.itemsList.lastSelectedItem);
        }
        break;
      case 9: // Tab
        // Eat any entered text and convert it to an item, and prevent focus shift.
        // If there isn't any text, let the tab go through as normal.
        if (currentText && currentText.length) {
          this._convertTextToSelectedItems();
          data.preventDefault();
        }
        break;
      case 13: // Enter
        // Eat any entered text and convert it to items.
        this._convertTextToSelectedItems();
        break;
      case 27: // Esc
        // Clear any entered text.
        this._nativeInput.value = '';
        break;
      case 32: // Space
        // The underlying ng-select component will handle the space event, trying to open
        // its popup and preventing spaces from appearing in the text. So we will manually
        // add a space here and then stop the ng-select from handling it.
        this._replaceSelectedText(this._nativeInput, ' ');
        data.preventDefault();
        break;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this._multiSelectComponent.ngOnChanges(changes);
  }

  writeValue(obj: any): void {
    this._multiSelectComponent.writeValue(obj);
    // If the value is replaced, we need to make sure that our items remain synced to prevent
    // old, now-unselected items from appearing in a popup.
    this._reIndex();
  }

  registerOnChange(fn: any): void {
    this._multiSelectComponent.registerOnChange(fn);
  }

  registerOnTouched(fn: any): void {
    this._multiSelectComponent.registerOnTouched(fn);
  }

  splitText(joinedText: string): string[] {
    let items: string[];

    let separators = (this.autoSeparators || [])
      .filter(s => s && s.length);

    if (separators.length) {
      if (separators.length === 1) {
        items = joinedText
          .split(separators[0]);
      } else {
        separators = separators.map(s => `(?:${CommonService.escapeRegexLiteral(s)})`);
        items = joinedText
          .split(new RegExp(separators.join('|')));
      }
    } else {
      items = [joinedText];
    }

    return items
      .map(item => item.trim())
      .filter(item => item.length);
  }

  private _syncComponent() {
    if (!this._multiSelectComponent) {
      return;
    }

    if (this._caseSensitive) {
      this._multiSelectComponent.compareWith = null;
    } else {
      this._multiSelectComponent.compareWith = (a, b) => CommonService.compareCaseInsensitive(this._locale, a, b);
    }
  }

  private _convertTextToSelectedItems() {
    const currentText = this._nativeInput.value as string;
    if (currentText && currentText.length) {
      const typedItems = this.splitText(currentText);

      this._nativeInput.value = '';

      const itemsList = this._multiSelectComponent.selectComponent.itemsList;

      for (const item of typedItems) {
        if (!itemsList.selectedItems.find(option => this._multiSelectComponent.compareWith(option.value, item))) {
          const newOption = itemsList.mapItem(item, itemsList.items.length);
          itemsList.items.push(newOption);
          this._multiSelectComponent.selectComponent.select(newOption);
        }
      }
    }
  }

  private _reIndex() {
    // This method ensures that the internal item list always has all items selected,
    // so the dropdown will not open.
    const itemsList = this._multiSelectComponent.selectComponent.itemsList;
    itemsList.items.length = 0;
    for (let i = 0; i < itemsList.selectedItems.length; i++) {
      itemsList.selectedItems[i].index = i;
      itemsList.items.push(itemsList.selectedItems[i]);
    }
  }

  private _replaceSelectedText(input: any, text: string) {
    if (!input) {
      return;
    }

    text = text || '';
    const success = document.execCommand('insertText', false, text);
    if (!success) {
      if (typeof input.selectionStart === 'number' && typeof input.selectionEnd === 'number') {
        const start = input.selectionStart;
        const end = input.selectionEnd;

        let value = input.value || '';
        value = value.slice(0, start) + text + value.slice(end);
        input.value = value;

        input.selectionStart = input.selectionEnd = start + text.length;

        const inputEvent = document.createEvent('UIEvent');
        inputEvent.initEvent('input', true, false);
        input.dispatchEvent(inputEvent);
      } else if ((document as any).selection) {
        const range = (document as any).selection.createRange();
        range.text = text;

        range.collapse(false);
        range.select();
      }
    }
  }
}
